/**
 * Dionysus - Reddit Growth Engine 🍷
 * 
 * The God of Viral Revelry - Where conversations become conversions.
 * 
 * THREE PILLARS:
 * 1. The Vineyard - Find opportunities (save 2 hours/day)
 * 2. The Chalice - Know what to say (don't get banned)  
 * 3. The Amphitheater - See what works (prove ROI)
 */

import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${API}/api`;

// ==================== OPPORTUNITY CARD ====================
const OpportunityCard = ({ opportunity, onDraftReply, onSkip }) => {
  const getIntentBadge = (intent) => {
    const badges = {
      high: { bg: 'bg-red-500/20', text: 'text-red-400', label: '🔥 HIGH INTENT' },
      medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: '⚡ MEDIUM' },
      low: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'LOW' },
      informational: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'ℹ️ INFO' }
    };
    return badges[intent] || badges.medium;
  };

  const getTimingBadge = (timing) => {
    const badges = {
      early: { bg: 'bg-green-500/20', text: 'text-green-400', label: '⚡ EARLY' },
      optimal: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: '✓ OPTIMAL' },
      late: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: '⏰ LATE' },
      stale: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: '💤 STALE' }
    };
    return badges[timing] || badges.optimal;
  };

  const intent = getIntentBadge(opportunity.intent_level);
  const timing = getTimingBadge(opportunity.timing);

  const formatTime = (utc) => {
    const now = new Date();
    const then = new Date(utc * 1000);
    const hours = Math.floor((now - then) / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    return `${Math.floor(hours / 24)} days ago`;
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:border-purple-500/50 transition-all">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-xs ${intent.bg} ${intent.text}`}>
              {intent.label}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs ${timing.bg} ${timing.text}`}>
              {timing.label}
            </span>
          </div>
          <h3 className="text-white font-medium line-clamp-2">{opportunity.title}</h3>
        </div>
        <div className="text-right text-xs text-gray-400">
          <div>r/{opportunity.subreddit}</div>
          <div>{formatTime(opportunity.created_utc)}</div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
        <span>⬆️ {opportunity.score}</span>
        <span>💬 {opportunity.num_comments}</span>
        <span className="text-purple-400 font-medium">
          Score: {Math.round(opportunity.opportunity_score)}
        </span>
      </div>

      {/* Pain Points */}
      {opportunity.pain_points?.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">Pain Points:</div>
          <div className="flex flex-wrap gap-1">
            {opportunity.pain_points.slice(0, 3).map((point, i) => (
              <span key={i} className="px-2 py-0.5 bg-red-500/10 text-red-300 rounded text-xs">
                {point}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <a
          href={opportunity.url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm transition-colors"
        >
          View Thread
        </a>
        <button
          onClick={() => onDraftReply(opportunity)}
          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded text-sm transition-colors"
        >
          Draft Reply
        </button>
        <button
          onClick={() => onSkip(opportunity.id)}
          className="px-3 py-1.5 text-gray-400 hover:text-white rounded text-sm transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

// ==================== REPLY DRAFT MODAL ====================
const ReplyDraftModal = ({ draft, onClose, onMarkPosted, onRegenerate, onHumanize, onCopy }) => {
  const [instructions, setInstructions] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isHumanizing, setIsHumanizing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');

  const handleCopy = async () => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(draft.content);
      } else {
        // Fallback for browsers/contexts where Clipboard API is blocked
        const textArea = document.createElement('textarea');
        textArea.value = draft.content;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.();
    } catch (err) {
      console.error('Copy failed:', err);
      // Final fallback - show content in a way user can manually copy
      alert('Copy to clipboard failed. Please manually select and copy the text.');
    }
  };

  const handleRegenerate = async () => {
    if (!instructions.trim()) return;
    setIsRegenerating(true);
    setRegenerateError('');
    try {
      await onRegenerate(draft.id, instructions);
      setInstructions('');
    } catch (err) {
      setRegenerateError(err.message || 'Failed to regenerate. Please try again.');
    }
    setIsRegenerating(false);
  };

  const handleHumanize = async () => {
    setIsHumanizing(true);
    await onHumanize(draft.id);
    setIsHumanizing(false);
  };

  const getRiskColor = (risk) => {
    switch(risk) {
      case 'low': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'high': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">💬 Draft Reply</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
          </div>
          <div className="text-sm text-gray-400 mt-1">
            r/{draft.subreddit} • {draft.thread_title}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {/* Scores */}
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">Authenticity:</span>
              <span className="text-green-400 font-bold">{draft.authenticity_score}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">Helpfulness:</span>
              <span className="text-blue-400 font-bold">{draft.helpfulness_score}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">Self-Promo Risk:</span>
              <span className={`font-bold uppercase ${getRiskColor(draft.self_promo_risk)}`}>
                {draft.self_promo_risk}
              </span>
            </div>
            {/* Character Counter */}
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">Characters:</span>
              <span className={`font-bold ${draft.content.length > 9000 ? 'text-red-400' : draft.content.length > 7000 ? 'text-yellow-400' : 'text-gray-300'}`}>
                {draft.content.length}/10,000
              </span>
            </div>
          </div>

          {/* Reply Text */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-white whitespace-pre-wrap">{draft.content}</p>
          </div>

          {/* Word Count */}
          <div className="mt-2 text-xs text-gray-500 flex justify-between">
            <span>{draft.word_count || draft.content.split(/\s+/).length} words</span>
            <span>Estimated read time: {Math.ceil((draft.word_count || draft.content.split(/\s+/).length) / 200)} min</span>
          </div>

          {/* Action Buttons Row */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleHumanize}
              disabled={isHumanizing}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded font-medium disabled:opacity-50 transition-all"
            >
              {isHumanizing ? '🔄 Humanizing...' : '🧬 Humanize'}
            </button>
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
            >
              {copied ? '✓ Copied!' : '📋 Copy'}
            </button>
          </div>

          {/* Regenerate with instructions */}
          <div className="mt-4">
            <label className="text-sm text-gray-400 block mb-2">
              Want specific changes? Tell Dionysus: <span className="text-purple-400">(3 credits)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={instructions}
                onChange={(e) => {
                  setInstructions(e.target.value);
                  setRegenerateError('');
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleRegenerate()}
                placeholder="e.g., Make it shorter, Add a question at the end..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white placeholder-gray-500"
              />
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating || !instructions.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded disabled:opacity-50 transition-colors"
              >
                {isRegenerating ? '🔄 Updating...' : '🔄 Regenerate'}
              </button>
            </div>
            {regenerateError && (
              <p className="text-red-400 text-sm mt-2">{regenerateError}</p>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-700 flex items-center gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => onMarkPosted(draft.id)}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded transition-colors font-medium"
          >
            ✅ Mark as Posted
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== STATS CARD ====================
const StatsCard = ({ label, value, icon, subtext }) => (
  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
    <div className="flex items-center justify-between">
      <span className="text-gray-400 text-sm">{label}</span>
      <span className="text-2xl">{icon}</span>
    </div>
    <div className="text-2xl font-bold text-white mt-1">{value}</div>
    {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
  </div>
);

// ==================== MAIN COMPONENT ====================
const DionysusPanel = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);
  
  // Config form
  const [configForm, setConfigForm] = useState({
    website_url: '',
    niche_keywords: '',
    brand_name: '',
    reddit_account_phase: 'seedling'
  });
  const [showConfig, setShowConfig] = useState(false);

  // Phase info for UI
  const phaseInfo = {
    seedling: { emoji: '🌱', label: 'Seedling', desc: '0-200 karma • Pure value only', color: 'text-green-400' },
    growing: { emoji: '🌿', label: 'Growing', desc: '200-500 karma • Soft openers OK', color: 'text-emerald-400' },
    established: { emoji: '🌳', label: 'Established', desc: '500-1000 karma • Can mention expertise', color: 'text-teal-400' },
    authority: { emoji: '👑', label: 'Authority', desc: '1000+ karma • Can link when asked', color: 'text-yellow-400' }
  };

  // Get auth token
  const getToken = () => localStorage.getItem('token');

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/dionysus/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          setError('Dionysus is a Pro+ feature. Upgrade to access.');
          return;
        }
        throw new Error('Failed to fetch dashboard');
      }
      
      const data = await response.json();
      setDashboard(data.dashboard);
      
      // Update config form
      if (data.dashboard?.config) {
        setConfigForm({
          website_url: data.dashboard.config.website_url || '',
          niche_keywords: (data.dashboard.config.niche_keywords || []).join(', '),
          brand_name: data.dashboard.config.brand_name || '',
          reddit_account_phase: data.dashboard.config.reddit_account_phase || 'seedling'
        });
      }
      
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch opportunities
  const fetchOpportunities = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/dionysus/opportunities`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
      }
    } catch (err) {
      console.error('Opportunities fetch error:', err);
    }
  }, []);

  // Save config
  const saveConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/dionysus/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          website_url: configForm.website_url,
          niche_keywords: configForm.niche_keywords.split(',').map(k => k.trim()).filter(Boolean),
          brand_name: configForm.brand_name,
          reddit_account_phase: configForm.reddit_account_phase
        })
      });
      
      if (response.ok) {
        setShowConfig(false);
        fetchDashboard();
      }
    } catch (err) {
      console.error('Config save error:', err);
    }
  };

  // Discover subreddits
  const discoverSubreddits = async () => {
    try {
      setIsDiscovering(true);
      const response = await fetch(`${API_BASE}/dionysus/discover-subreddits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          website_url: configForm.website_url,
          niche_keywords: configForm.niche_keywords.split(',').map(k => k.trim()).filter(Boolean)
        })
      });
      
      if (response.ok) {
        fetchDashboard();
      }
    } catch (err) {
      console.error('Discovery error:', err);
    } finally {
      setIsDiscovering(false);
    }
  };

  // Scan for opportunities
  const scanOpportunities = async () => {
    try {
      setIsScanning(true);
      const response = await fetch(`${API_BASE}/dionysus/scan-opportunities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
        fetchDashboard();
      }
    } catch (err) {
      console.error('Scan error:', err);
    } finally {
      setIsScanning(false);
    }
  };

  // Generate reply
  const generateReply = async (opportunity) => {
    try {
      setIsDrafting(true);
      const response = await fetch(`${API_BASE}/dionysus/generate-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          thread_id: opportunity.id,
          thread_url: opportunity.url,
          thread_title: opportunity.title,
          subreddit: opportunity.subreddit,
          tone: 'helpful',
          include_soft_cta: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedDraft(data.draft);
      }
    } catch (err) {
      console.error('Reply generation error:', err);
    } finally {
      setIsDrafting(false);
    }
  };

  // Regenerate reply
  const regenerateReply = async (draftId, instructions) => {
    try {
      const response = await fetch(`${API_BASE}/dionysus/drafts/${draftId}/regenerate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ instructions })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSelectedDraft(data.draft);
        // Refresh dashboard to get updated credits
        fetchDashboard();
      } else {
        throw new Error(data.detail || data.message || 'Failed to regenerate reply');
      }
    } catch (err) {
      console.error('Regenerate error:', err);
      throw err;
    }
  };

  // Mark as posted
  const markPosted = async (draftId) => {
    try {
      const response = await fetch(`${API_BASE}/dionysus/drafts/${draftId}/mark-posted`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setSelectedDraft(null);
        fetchDashboard();
      }
    } catch (err) {
      console.error('Mark posted error:', err);
    }
  };

  // Humanize reply - make it sound more natural
  const humanizeReply = async (draftId) => {
    try {
      const response = await fetch(`${API_BASE}/dionysus/drafts/${draftId}/humanize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedDraft(data.draft);
      }
    } catch (err) {
      console.error('Humanize error:', err);
    }
  };

  // Skip opportunity
  const skipOpportunity = async (threadId) => {
    try {
      await fetch(`${API_BASE}/dionysus/opportunities/${threadId}/skip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      setOpportunities(prev => prev.filter(o => o.id !== threadId));
    } catch (err) {
      console.error('Skip error:', err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchDashboard();
    fetchOpportunities();
  }, [fetchDashboard, fetchOpportunities]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🍷</div>
          <div className="text-white">Dionysus is awakening...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <div className="text-white mb-2">{error}</div>
          <button
            onClick={() => { setError(null); fetchDashboard(); }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const highIntent = opportunities.filter(o => o.intent_level === 'high');

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              🍷 Dionysus
              <span className="text-lg font-normal text-purple-400">Reddit Growth Engine</span>
            </h1>
            <p className="text-gray-400 mt-1">
              Find opportunities • Craft replies • Track results
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Current Phase Badge */}
            <div className={`px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 flex items-center gap-2 ${phaseInfo[configForm.reddit_account_phase]?.color || 'text-gray-400'}`}>
              <span className="text-lg">{phaseInfo[configForm.reddit_account_phase]?.emoji || '🌱'}</span>
              <span className="font-medium">{phaseInfo[configForm.reddit_account_phase]?.label || 'Seedling'}</span>
            </div>
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700"
            >
              ⚙️ Settings
            </button>
          </div>
        </div>

        {/* Config Panel */}
        {showConfig && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">🍇 Configure The Vineyard</h2>
            
            {/* Reddit Account Phase Selector */}
            <div className="mb-6 p-4 bg-gray-900/50 rounded-lg border border-gray-600">
              <label className="text-sm text-gray-300 block mb-3 font-medium">
                🎮 Reddit Account Phase
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(phaseInfo).map(([key, info]) => (
                  <button
                    key={key}
                    onClick={() => setConfigForm(prev => ({ ...prev, reddit_account_phase: key }))}
                    className={`p-3 rounded-lg border transition-all text-left ${
                      configForm.reddit_account_phase === key
                        ? 'bg-purple-600/30 border-purple-500 ring-2 ring-purple-500/50'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-2xl mb-1">{info.emoji}</div>
                    <div className={`font-medium ${info.color}`}>{info.label}</div>
                    <div className="text-xs text-gray-400 mt-1">{info.desc}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                💡 This determines how Dionysus crafts your replies. Start as Seedling and progress as your account grows!
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Website URL</label>
                <input
                  type="text"
                  value={configForm.website_url}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, website_url: e.target.value }))}
                  placeholder="https://yoursite.com"
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Niche Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={configForm.niche_keywords}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, niche_keywords: e.target.value }))}
                  placeholder="SaaS, AI, marketing"
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Brand Name</label>
                <input
                  type="text"
                  value={configForm.brand_name}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, brand_name: e.target.value }))}
                  placeholder="Your Brand"
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={saveConfig}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded"
              >
                Save Configuration
              </button>
              <button
                onClick={discoverSubreddits}
                disabled={isDiscovering}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50"
              >
                {isDiscovering ? '🔍 Discovering...' : '🔍 Discover Subreddits (10 credits)'}
              </button>
            </div>
            {dashboard?.subreddits_tracked > 0 && (
              <div className="mt-4 text-sm text-gray-400">
                ✓ Tracking {dashboard.subreddits_tracked} subreddits
              </div>
            )}
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatsCard
            label="Today's Opportunities"
            value={opportunities.length}
            icon="🎯"
            subtext={`${highIntent.length} high-intent`}
          />
          <StatsCard
            label="Replies Posted"
            value={stats.replies_posted || 0}
            icon="💬"
            subtext="This week"
          />
          <StatsCard
            label="Upvotes Earned"
            value={stats.total_upvotes || 0}
            icon="⬆️"
          />
          <StatsCard
            label="Brand Mentions"
            value={stats.brand_mentions || 0}
            icon="📣"
            subtext={stats.negative_mentions > 0 ? `${stats.negative_mentions} need attention` : undefined}
          />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Opportunities List */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">🔥 Today's Opportunities</h2>
              <button
                onClick={scanOpportunities}
                disabled={isScanning}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded disabled:opacity-50"
              >
                {isScanning ? '🔄 Scanning...' : '🔄 Scan Now (5 credits)'}
              </button>
            </div>

            {opportunities.length === 0 ? (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8 text-center">
                <div className="text-4xl mb-4">🍇</div>
                <h3 className="text-white text-lg mb-2">No opportunities yet</h3>
                <p className="text-gray-400 mb-4">
                  Configure your niche and scan for opportunities
                </p>
                <button
                  onClick={scanOpportunities}
                  disabled={isScanning}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded"
                >
                  {isScanning ? 'Scanning...' : 'Scan for Opportunities'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {opportunities.map((opp) => (
                  <OpportunityCard
                    key={opp.id}
                    opportunity={opp}
                    onDraftReply={generateReply}
                    onSkip={skipOpportunity}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Brand Pulse */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-bold text-white mb-3">🎯 Brand Pulse</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Mentions Found</span>
                  <span className="text-white font-bold">{stats.brand_mentions || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Sentiment</span>
                  <span className="text-green-400">
                    {stats.positive_mentions > stats.negative_mentions ? '😊 Positive' : '😐 Neutral'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Competitor Mentions</span>
                  <span className="text-white">{stats.competitor_mentions || 0}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-bold text-white mb-3">⚡ Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={scanOpportunities}
                  disabled={isScanning}
                  className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-left disabled:opacity-50"
                >
                  🔍 Scan Opportunities
                </button>
                <button
                  onClick={discoverSubreddits}
                  disabled={isDiscovering}
                  className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-left disabled:opacity-50"
                >
                  🍇 Discover Subreddits
                </button>
              </div>
            </div>

            {/* Pro Tips */}
            <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
              <h3 className="text-lg font-bold text-purple-300 mb-2">💡 Pro Tips</h3>
              <ul className="text-sm text-gray-300 space-y-2">
                <li>• Reply to EARLY threads (under 2 hours) for max visibility</li>
                <li>• Focus on HIGH INTENT posts - they convert better</li>
                <li>• Never include links in your first reply</li>
                <li>• Add value first, mention your product only if asked</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Draft Modal */}
      {selectedDraft && (
        <ReplyDraftModal
          draft={selectedDraft}
          onClose={() => setSelectedDraft(null)}
          onMarkPosted={markPosted}
          onRegenerate={regenerateReply}
          onHumanize={humanizeReply}
        />
      )}

      {/* Drafting Overlay */}
      {isDrafting && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-pulse">🍷</div>
            <div className="text-white">Dionysus is crafting your reply...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DionysusPanel;
